import { Component, Injector, Input, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import {
  AbstractComponent,
  Channel,
  ChannelEvent,
  ChannelHealth,
  ChannelHealthClassificationEnum,
  ChannelHealthPendingIssue,
  ChannelHealthService,
  ChannelService,
  ConfirmationComponent,
} from 'lib-trend-core';
import { takeUntil } from 'rxjs';
import { ChannelEditComponent } from '../../channel-edit/channel-edit.component';

// 'hibrido' only on a Hybrid channel (SPEC-2026-10-whatsapp-hybrid-channel RF-02, RF-26).
type HealthTabId = 'resumo' | 'saude' | 'qualidade' | 'perfil' | 'hibrido';

/**
 * Cloud API detail shell (SPEC-2026-08-channel-health, T-20 — RF-15/RF-16/RF-36).
 *
 * Four sub-areas — Resumo · Saúde do Canal · Qualidade e Entrega · Perfil do WhatsApp —
 * with the reading's freshness and the per-channel "Atualizar agora" (same shared lock as
 * the cron: a refresh already in flight answers 409 and is surfaced as-is). The profile
 * area renders only for administrators; the shell itself is only rendered for
 * administrators and supervisors (gated by the host view).
 */
@Component({
    selector: 'channel-health-dashboard',
    templateUrl: './channel-health-dashboard.component.html',
    styleUrl: './channel-health-dashboard.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ChannelHealthDashboardComponent extends AbstractComponent implements OnInit {

  @Input({ required: true }) channel: Channel;

  activeTab: HealthTabId = 'resumo';

  health?: ChannelHealth;
  pendingIssues: Array<ChannelHealthPendingIssue> = [];
  events: Array<ChannelEvent> = [];

  refreshing: boolean = false;
  /** Bumped after each successful refresh so the active tab re-fetches its endpoint. */
  refreshTick: number = 0;
  /** Raised while a connection action runs — same semantics as the old dashboard's `loading`. */
  actionLoading: boolean = false;

  classification = ChannelHealthClassificationEnum;

  constructor(
    public injector: Injector,
    public dialog: MatDialog,
    private channelHealthService: ChannelHealthService,
    private channelService: ChannelService,
  ) {
    super(injector);
  }

  ngOnInit(): void {
    this.loadDetail();
  }

  activate(tab: HealthTabId): void {
    if (tab === 'perfil' && !this.canSeeProfile()) return;
    this.activeTab = tab;
  }

  /**
   * The WhatsApp profile is read from and written to Meta with the channel's token: admin-only
   * (RF-36), and only while the official side is connected — without a token the profile routes
   * fail.
   */
  canSeeProfile(): boolean {
    return this.isAdmin() && this.channel?.connected === true;
  }

  loadDetail(): void {
    this.channelHealthService.getHealthDetail(this.channel._id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (detail) => {
          this.health = detail.channel?.health;
          this.pendingIssues = detail.pendingIssues ?? [];
          this.events = detail.events ?? [];
        },
        error: (err) => this.alertService.error(err?.error?.message),
      });
  }

  /** RF-15: on-demand refresh scoped to this channel. */
  refreshNow(): void {
    if (this.refreshing) return;
    this.refreshing = true;
    this.channelHealthService.refreshChannel(this.channel._id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.refreshing = false;
          if (response.channel) this.channel = response.channel;
          this.refreshTick++;
          this.loadDetail();
        },
        error: (err) => {
          this.refreshing = false;
          // 409 = a sync for this channel is already running (shared lock) — provider message as-is.
          this.alertService.error(err?.error?.message);
        },
      });
  }

  /** System status-badge variants (`.campaign-badge` — the shared standard). */
  badgeClass(): string {
    const map: Record<string, string> = {
      [ChannelHealthClassificationEnum.HEALTHY]: 'is-ok',
      [ChannelHealthClassificationEnum.ATTENTION]: 'is-warn',
      [ChannelHealthClassificationEnum.CRITICAL]: 'is-err',
      [ChannelHealthClassificationEnum.NO_DATA]: 'is-neutral',
    };
    return map[this.health?.classification ?? ChannelHealthClassificationEnum.NO_DATA];
  }

  badgeLabel(): string {
    return this.t(`channel.health.badge.${this.health?.classification ?? ChannelHealthClassificationEnum.NO_DATA}`);
  }

  // ------------------------------------------------------- channel actions (same as channel-config)

  /** Same behavior as the old dashboard: confirmation → delete → back to the listing. */
  onDelete(channel: Channel): void {
    const dialogRef = this.dialog.open(ConfirmationComponent, {
      width: '600px'
    });
    dialogRef.afterClosed().subscribe(result => {
      if (Boolean(result) === true) {
        this.channelService.delete(channel._id).subscribe({
          next: () => {
            this.alertService.success();
            this.router.navigate(['/channel']);
          },
          error: err => this.alertService.error(err.error.message)
        });
      }
    });
  }

  /**
   * Disconnects without deleting (tokens cleared, channel and history kept) — the Cloud API
   * warning text and the service call are the ones the old dashboard used.
   */
  disconnect(): void {
    const dialogRef = this.dialog.open(ConfirmationComponent, {
      width: '600px',
      data: this.t('channel.channelConfig.disconnectMetaConfirm'),
    });
    dialogRef.afterClosed().subscribe(result => {
      if (Boolean(result) === true) {
        this.actionLoading = true;
        this.channelService.disconnectInstanceByChannel(this.channel._id).subscribe({
          next: (channel: Channel) => {
            this.channel = channel;
            this.alertService.success(this.t('channel.channelConfig.disconnectSuccess'));
          },
          error: (err) => {
            this.alertService.error(err.error?.message ?? this.t('channel.channelConfig.disconnectError'));
            this.actionLoading = false;
          },
          complete: () => this.actionLoading = false,
        });
      }
    });
  }

  /**
   * The channel came back from the Embedded Signup — connect (new channel) or reconnect.
   * Same hook as the old dashboard; the tabs re-fetch through `refreshTick`.
   */
  onEmbeddedConnected(channel: Channel): void {
    this.channel = channel;
    this.refreshTick++;
    this.loadDetail();
  }

  /** Same edit dialog as the old dashboard (which does nothing on close). */
  edit(item: Channel): void {
    const dialogRef = this.dialog.open(ChannelEditComponent, {
      width: '600px',
      data: item
    });
    dialogRef.afterClosed().subscribe(result => {
    });
  }

  /** RF-15: "atualizado há N min". */
  freshnessLabel(): string | null {
    const readAt = this.health?.readAt;
    if (!readAt) return null;
    const minutes = Math.max(0, Math.floor((Date.now() - new Date(readAt).getTime()) / 60000));
    if (minutes < 60) return this.t('channel.health.freshness.updatedMinutes', { minutes });
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return this.t('channel.health.freshness.updatedHours', { hours });
    const date = new Date(readAt);
    return this.t('channel.health.freshness.lastConsult', {
      date: `${date.toLocaleDateString()} ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`,
    });
  }
}
